import { Skeleton } from 'antd';

import calenderTick from './../../../../../src/assets/img/calender-tick.png';
import { TARGET_BLANK } from 'utils/constants/routes';
import { ROUTE_PATHS } from 'utils/constants/routes';
import { Link } from 'react-router-dom';
import { IScheduleLineItem } from 'model/v2';

const TableSkeleton = () => (
  <Skeleton active={true} paragraph={{ rows: 0, width: '100%' }} />
);

const formatOpensOnDate = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `Week of ${formatted}`;
};

const RemainingScheduleCell = ({ items }: { items: IScheduleLineItem[] }) => {
  if (items.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <img src={calenderTick} alt="calenderTick" width={20} />
        <span style={{ marginLeft: 4, marginTop: 4 }}>Booked !</span>
      </div>
    );
  }
  return (
    <div>
      {items.map((item, idx) => {
        const lines = item.text.split('\n');
        const opensLabel = item.opensOn ? ` [Opens: ${formatOpensOnDate(item.opensOn)}]` : '';
        // Only show non-BT tag if opensOn is not present
        const nonBTLabel = !item.opensOn && item.hasNonBTBooking ? ' [non-BT]' : '';
        return (
          <div key={idx} style={{ marginBottom: idx < items.length - 1 ? 4 : 0 }}>
            <div>{lines[0]}{opensLabel}{nonBTLabel}</div>
            {lines.slice(1).map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

interface IMatchesTabRow {
  bt: string;
  acceptedSetShift: string[];
  remainingSetShift: IScheduleLineItem[];
  acceptedAdditional: string[];
  remainingAdditional: IScheduleLineItem[];
  btId: number;
}

interface Note {
  id: number;
  actionAt: string;
  note: string;
  author: string;
}

interface ClientRow {
  client: string;
  acceptedBlock: string;
  remainingBlocks: string;
  remainingBlockItems?: IScheduleLineItem[];
  status: string;
  notes: Note[];
  notesCount: number;
  clientId: string;
  name: string;
  isLoaderView?: boolean;
  link?: boolean;
}

export const useMatchesTabColumns = () => {
  const clientColumns = [
    {
      title: 'Client',
      dataIndex: 'client',
      key: 'client',
      width: '40%',
      render: (text: string, record: ClientRow) => {
        const { isLoaderView } = record;

        if (isLoaderView) {
          return <TableSkeleton />;
        }

        return record.link ? (
          <Link
            className="schedule_link"
            to={ROUTE_PATHS.CLIENT_AVAILABILITY + record?.clientId}
            target={TARGET_BLANK}
            style={{
              textTransform: 'none',
              padding: 0,
              minWidth: 0,
              color: '#6f42f5',
              cursor: 'pointer'
            }}
          >
            {text}
          </Link>
        ) : (
          text
        );
      }
    },
    {
      title: 'Recommended',
      dataIndex: 'recommended',
      key: 'recommended',
      render: (text: string) => {
        return text;
      }
    },
    {
      title: 'Accepted Block',
      dataIndex: 'acceptedBlock',
      key: 'acceptedBlock',
      width: '40%',
      render: (text: string, record: ClientRow) => {
        const { isLoaderView } = record;

        if (isLoaderView) {
          return <TableSkeleton />;
        }

        if (!text || text.trim() === '') {
          return <div style={{ color: '#999' }}>None</div>;
        }

        return text.split('\n').map((block: string, blockIndex: number) => (
          <div
            key={blockIndex}
            style={{
              marginBottom: blockIndex > 0 ? '8px' : '0'
            }}
          >
            {block.split('\n').map((line, i) => (
              <div key={i}>{line}</div>
            ))}
          </div>
        ));
      }
    },
    {
      title: 'Remaining Blocks',
      dataIndex: 'remainingBlocks',
      key: 'remainingBlocks',
      width: '40%',
      render: (_text: string, record: ClientRow) => {
        const { isLoaderView } = record;

        if (isLoaderView) {
          return <TableSkeleton />;
        }

        const items = record.remainingBlockItems;
        if (!items || items.length === 0) {
          return <div style={{ color: '#999' }}>None</div>;
        }

        return <RemainingScheduleCell items={items} />;
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '40%',
      render: (text: string, record: ClientRow) => {
        const { isLoaderView } = record;

        if (isLoaderView) {
          return <TableSkeleton />;
        }

        return <div style={{ whiteSpace: 'normal' }}>{text}</div>;
      }
    },
    {
      title: 'Waitlisted Reason',
      dataIndex: 'waitlistedReason',
      key: 'waitlistedReason',
      render: (text: string) => {
        return text;
      }
    }
  ];

  const setShiftColumns = [
    {
      title: 'BT',
      dataIndex: 'bt',
      key: 'bt',
      width: '30%',
      render: (text: string, row: IMatchesTabRow) => {
        const providerProfileUrl = `${ROUTE_PATHS.PROVIDER}${row.btId}`;
        return (
          <Link
            to={providerProfileUrl}
            target={TARGET_BLANK}
            style={{
              textTransform: 'none',
              padding: 0,
              minWidth: 0,
              color: '#6f42f5',
              cursor: 'pointer'
            }}
          >
            {text}
          </Link>
        );
      }
    },
    {
      title: 'Accepted Set Shift',
      dataIndex: 'acceptedSetShift',
      key: 'acceptedSetShift',
      width: '33%',
      render: (text: string[]) => {
        return text.length > 0 ? (
          text.map((line: string) => {
            return line
              .split('\n')
              .map((line: string, i: number) => <div key={i}>{line}</div>);
          })
        ) : (
          <div>None</div>
        );
      }
    },
    {
      title: 'Remaining Set Shift',
      dataIndex: 'remainingSetShift',
      key: 'remainingSetShift',
      width: '33%',
      render: (items: IScheduleLineItem[], record: IMatchesTabRow) => {
        if (record.acceptedSetShift.length === 0) {
          return <div>None</div>;
        }
        return <RemainingScheduleCell items={items} />;
      }
    }
  ];

  const additionalColumns = [
    {
      title: 'BT',
      dataIndex: 'bt',
      key: 'bt',
      width: '30%',
      render: (text: string, row: IMatchesTabRow) => {
        const providerProfileUrl = `${ROUTE_PATHS.PROVIDER}${row.btId}`;
        return (
          <Link
            to={providerProfileUrl}
            target={TARGET_BLANK}
            style={{
              textTransform: 'none',
              padding: 0,
              minWidth: 0,
              color: '#6f42f5',
              cursor: 'pointer'
            }}
          >
            {text}
          </Link>
        );
      }
    },
    {
      title: 'Accepted Additional',
      dataIndex: 'acceptedAdditional',
      key: 'acceptedAdditional',
      width: '33%',
      render: (text: string[]) => {
        return text.length > 0 ? (
          text.map((line: string) => {
            return line
              .split('\n')
              .map((line: string, i: number) => <div key={i}>{line}</div>);
          })
        ) : (
          <div>None</div>
        );
      }
    },
    {
      title: 'Remaining Additional',
      dataIndex: 'remainingAdditional',
      key: 'remainingAdditional',
      width: '33%',
      render: (items: IScheduleLineItem[], record: IMatchesTabRow) => {
        if (record.acceptedAdditional.length === 0) {
          return <div>None</div>;
        }
        return <RemainingScheduleCell items={items} />;
      }
    }
  ];

  return {
    clientColumns,
    setShiftColumns,
    additionalColumns
  };
};
